import { Box } from '@mui/material';
import IncDonutChart from 'components/charts/inc-donut-chart/inc-donut-chart.component';
import PreviousPeriodDataCard from 'components/data-card/variants/previous-period-data-card/previous-period-data-card.component';
import { useSearchQueryContext } from 'components/search/search.hooks';
import { useTranslation } from 'react-i18next';
import type { SummaryCardsConfig } from './email-pulse-summary-cards.types';
import { useChartDataWithTranslations } from 'components/charts/inc-charts.hooks';

type EmailPulseSummaryCardsProps = {
  config: SummaryCardsConfig;
};

const EmailPulseSummaryCards = ({ config }: EmailPulseSummaryCardsProps) => {
  const { t } = useTranslation();
  const { summaryQueryName, leftChart, rightChart, metricCards } = config;

  // Fetch summary data
  const {
    data: summaryData,
    queryState: { isFetching: isSummaryFetching },
  } = useSearchQueryContext(summaryQueryName);

  // Fetch left chart data
  const {
    data: openedChartData,
    queryState: { isFetching: isLeftChartFetching },
  } = useSearchQueryContext(leftChart.queryName);

  // Fetch right chart data
  const {
    data: bouncedChartData,
    queryState: { isFetching: isRightChartFetching },
  } = useSearchQueryContext(rightChart.queryName);

  const openedTranslatedData = useChartDataWithTranslations(
    openedChartData?.data,
    label => `page.emailPulse.${label}.label`,
  );

  const bouncedTranslatedData = useChartDataWithTranslations(
    bouncedChartData?.data,
    label => `page.emailPulse.${label}.label`,
  );

  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        xs: '1fr',
        lg: '2.5fr 4fr 2.5fr',
        xl: '1.5fr 4fr 1.5fr',
      }}
      gap={2}
    >
      {/* Left Chart */}
      <Box display="grid" height={{ xs: 300, xl: 'auto' }}>
        <IncDonutChart
          data={openedTranslatedData}
          title={leftChart.definition.getLabel(t as any)}
          data-testid={leftChart.testId}
          noDataMessage={t('page.emailPulse.totalEmailsOpened.noDataMessage')}
          PieChartProps={{
            loading: isLeftChartFetching,
            sx: {
              maxHeight: { xs: 'unset', lg: 190 },
            },
          }}
        />
      </Box>

      {/* Metric Cards */}
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          xl: 'repeat(3, 1fr)',
        }}
        gridAutoRows="1fr"
        gap={2}
      >
        {metricCards.map(cardConfig => {
          const cardData = summaryData?.[cardConfig.dataField];
          return (
            <PreviousPeriodDataCard
              key={cardConfig.dataField}
              title={cardConfig.definition.getLabel(t as any)}
              value={cardData?.value}
              valueType={cardData?.cardType}
              previousPeriodPercent={cardData?.comparisonNumber}
              PreviousPeriodDataCardDescriptionProps={
                cardConfig.definition.invertColor
                  ? {
                      invertColor: true,
                    }
                  : undefined
              }
              loading={isSummaryFetching}
            />
          );
        })}
      </Box>

      {/* Right Chart */}
      <Box display="grid" height={{ xs: 300, xl: 'auto' }}>
        <IncDonutChart
          data={bouncedTranslatedData}
          title={rightChart.definition.getLabel(t as any)}
          data-testid={rightChart.testId}
          noDataMessage={t('page.emailPulse.emailsBounced.noDataMessage')}
          PieChartProps={{
            loading: isRightChartFetching,
            sx: {
              maxHeight: { xs: 'unset', lg: 190 },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default EmailPulseSummaryCards;
